import { Check, Circle, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { SpinnerIcon } from '@/components/ui'
import { cn } from '@/lib/utils'

import type { Milestone, MilestoneState } from '../wizardSteps'

function MilestoneIcon({ state }: { readonly state: MilestoneState }) {
  if (state === 'done') {
    return <Check aria-hidden="true" className="h-4 w-4 shrink-0 text-status-success" />
  }

  if (state === 'error') {
    return <X aria-hidden="true" className="h-4 w-4 shrink-0 text-status-danger" />
  }

  if (state === 'active') {
    return <SpinnerIcon size="sm" className="shrink-0 text-action-primary" />
  }

  return <Circle aria-hidden="true" className="h-4 w-4 shrink-0 text-foreground-muted" />
}

export interface ProvisioningProgressProps {
  readonly milestones: readonly Milestone[]
}

/**
 * Checklist legivel do avanco da saga ("Configurando seu atendimento..."), no mesmo padrao
 * visual do checklist de descoberta do `.dc.html` (secao Passo 2) — aqui aplicado ao progresso
 * real do provisioning, nunca a passos de IA ainda nao implementados.
 */
export function ProvisioningProgress({ milestones }: ProvisioningProgressProps) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-foreground-primary">
        {t('onboarding.connect.progress.title')}
      </h3>
      <div role="list" className="flex flex-col gap-2">
        {milestones.map((milestone) => (
          <div
            key={milestone.id}
            role="listitem"
            className={cn(
              'flex items-center gap-3 rounded-lg border px-4 py-3 text-sm transition-colors motion-reduce:transition-none',
              milestone.state === 'error'
                ? 'border-status-danger/40 bg-status-danger-subtle'
                : milestone.state === 'active'
                  ? 'border-border-strong bg-background-subtle'
                  : 'border-border-default',
            )}
          >
            <MilestoneIcon state={milestone.state} />
            <span
              className={cn(
                'flex-1',
                milestone.state === 'pending' ? 'text-foreground-muted' : 'text-foreground-primary',
              )}
            >
              {t(milestone.labelKey)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
